import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Calendar,
  Target,
  Briefcase,
  Flag,
  Users,
  Gift,
  X,
  PlusCircle,
  Rocket,
  Image,
  Clock,
  AlertCircle,
  ChevronRight,
  Globe,
  Github,
  Database,
  Trash2,
  Loader2,
  Save,
  ImageIcon
} from "lucide-react";
import axios from "axios";
import ImageUpload from "../components/ImageUpload";

// Initial state with all required fields
const initialFormData = {
  title: "",
  tagline: "",
  description: {
    short: "",
    detailed: "",
  },
  category: "startup",
  subcategory: "",
  timeline: {
    startDate: "",
    endDate: "",
    milestones: [],
  },
  financials: {
    targetAmount: "",
    budget: [],
    rewards: [],
  },
  team: [],
  projectLinks: {
    demo: "",
    prototype: "",
    github: "",
    driveFolder: "",
    designFiles: "",
    additionalLinks: [],
  },
  technicalDetails: {
    stack: [],
    architecture: "",
    apis: [],
    infrastructure: [],
    deployment: {
      platform: "",
      url: "",
      instructions: "",
    },
  },
  impact: {
    beneficiaries: [],
    metrics: [],
    outcomes: [],
  },
  media: {
    images: [],  // Will contain objects with {url, caption, isPrimary}
    videos: [],
    documents: [],
  },
};

// Form steps configuration
const steps = [
  {
    id: 1,
    title: "Basic Details",
    icon: Flag,
  },
  {
    id: 2,
    title: "Timeline & Milestones",
    icon: Clock,
  },
  {
    id: 3,
    title: "Financial Details",
    icon: Gift,
  },
  {
    id: 4,
    title: "Team & Requirements",
    icon: Users,
  },
  {
    id: 5,
    title: "Project Details",
    icon: Briefcase,
  },
  {
    id: 6,
    title: "Impact & Goals",
    icon: Target,
  },
  {
    id: 7,
    title: "Media & Documentation",
    icon: Image,
  },
];

const CampaignForm = () => {
  const { id } = useParams(); // Get campaign ID if editing
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!id);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState(initialFormData);

  // Add useEffect to fetch campaign data when editing
  useEffect(() => {
    if (id) {
      fetchCampaignDetails();
    }
  }, [id]);

  const fetchCampaignDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/campaigns/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const campaignData = response.data;

      // Transform API data to match form structure
      setFormData({
        title: campaignData.title || "",
        tagline: campaignData.tagline || "",
        description: {
          short: campaignData.description?.short || "",
          detailed: campaignData.description?.detailed || "",
        },
        category: campaignData.category || "startup",
        subcategory: campaignData.subcategory || "",
        timeline: {
          startDate: campaignData.timeline?.startDate
            ? new Date(campaignData.timeline.startDate)
                .toISOString()
                .slice(0, 10)
            : "",
          endDate: campaignData.timeline?.endDate
            ? new Date(campaignData.timeline.endDate).toISOString().slice(0, 10)
            : "",
          milestones: campaignData.timeline?.milestones || [],
        },
        financials: {
          targetAmount: campaignData.financials?.targetAmount || "",
          budget: campaignData.financials?.budget || [],
          rewards: campaignData.financials?.rewards || [],
        },
        team: campaignData.organizer?.team || [],
        projectLinks: campaignData.projectLinks || {
          demo: "",
          prototype: "",
          github: "",
          driveFolder: "",
          designFiles: "",
          additionalLinks: [],
        },
        technicalDetails: campaignData.technicalDetails || {
          stack: [],
          architecture: "",
          apis: [],
          infrastructure: [],
          deployment: {
            platform: "",
            url: "",
            instructions: "",
          },
        },
        impact: campaignData.impact || {
          beneficiaries: [],
          metrics: [],
          outcomes: [],
        },
        media: campaignData.media || {
          images: [],
          videos: [],
          documents: [],
        },
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setInitialLoading(false);
    }
  };

  // Basic field change handler
  const handleChange = (section, subsection, value) => {
    setFormData((prev) => {
      if (subsection) {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [subsection]: value,
          },
        };
      }
      return {
        ...prev,
        [section]: value,
      };
    });
  };

  // Milestone handlers
  const addMilestone = () => {
    setFormData((prev) => ({
      ...prev,
      timeline: {
        ...prev.timeline,
        milestones: [
          ...prev.timeline.milestones,
          {
            title: "",
            description: "",
            targetDate: "",
            deliverables: [],
          },
        ],
      },
    }));
  };

  const updateMilestone = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      timeline: {
        ...prev.timeline,
        milestones: prev.timeline.milestones.map((milestone, i) =>
          i === index ? { ...milestone, [field]: value } : milestone
        ),
      },
    }));
  };

  const removeMilestone = (index) => {
    setFormData((prev) => ({
      ...prev,
      timeline: {
        ...prev.timeline,
        milestones: prev.timeline.milestones.filter((_, i) => i !== index),
      },
    }));
  };

  // Team handlers
  const addTeamMember = () => {
    setFormData((prev) => ({
      ...prev,
      team: [
        ...prev.team,
        {
          role: "",
          responsibilities: [],
          required: true,
          skills: [],
          experience: "",
        },
      ],
    }));
  };

  const updateTeamMember = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      team: prev.team.map((member, i) =>
        i === index ? { ...member, [field]: value } : member
      ),
    }));
  };

  const removeTeamMember = (index) => {
    setFormData((prev) => ({
      ...prev,
      team: prev.team.filter((_, i) => i !== index),
    }));
  };

  // Budget handlers
  const addBudgetItem = () => {
    setFormData((prev) => ({
      ...prev,
      financials: {
        ...prev.financials,
        budget: [
          ...prev.financials.budget,
          {
            category: "",
            amount: "",
            description: "",
          },
        ],
      },
    }));
  };

  const updateBudgetItem = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      financials: {
        ...prev.financials,
        budget: prev.financials.budget.map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        ),
      },
    }));
  };

  const removeBudgetItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      financials: {
        ...prev.financials,
        budget: prev.financials.budget.filter((_, i) => i !== index),
      },
    }));
  };
  const handleStepChange = (direction) => {
    if (direction === 'next' && currentStep < steps.length) {
      setCurrentStep(prev => prev + 1);
    } else if (direction === 'prev' && currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (currentStep !== steps.length) {
      setCurrentStep(currentStep + 1);
      return;
    }
  
    setLoading(true);
    setError("");
  
    try {
      const token = localStorage.getItem("token");
      const url = id
        ? `${process.env.REACT_APP_BASE_URL}/api/campaigns/${id}`
        : `${process.env.REACT_APP_BASE_URL}/api/campaigns`;
  
      // Ensure images data is properly formatted
      const submissionData = {
        ...formData,
        media: {
          ...formData.media,
          images: formData.media.images.map((img, index) => {
            // If img is just a URL string, convert it to the proper object format
            if (typeof img === 'string') {
              return {
                url: img,
                caption: `Image ${index + 1}`,
                isPrimary: index === 0
              };
            }
            // If it's already an object, ensure it has all required fields
            return {
              url: img.url,
              caption: img.caption || `Image ${index + 1}`,
              isPrimary: img.isPrimary || index === 0
            };
          }).filter(img => img.url) // Remove any empty image slots
        }
      };
  
      const response = await axios({
        method: id ? "put" : "post",
        url,
        data: submissionData,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
  
      alert(`Campaign ${id ? "updated" : "created"} successfully!`);
      navigate("/campaigns");
    } catch (err) {
      setError(
        err.response?.data?.message ||
          `Failed to ${id ? "update" : "create"} campaign`
      );
      console.error("API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Add loading state
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <p className="text-gray-600 animate-pulse">
            Loading campaign details...
          </p>
        </div>
      </div>
    );
  }

  const renderBasicDetails = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700 block mb-1.5">
              Campaign Title
            </span>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange("title", null, e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
              placeholder="Enter a compelling title"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-gray-700 block mb-1.5">
              Tagline
            </span>
            <input
              type="text"
              value={formData.tagline}
              onChange={(e) => handleChange("tagline", null, e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
              placeholder="A brief, catchy description"
            />
          </label>
        </div>

        <div className="space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700 block mb-1.5">
              Category
            </span>
            <select
              value={formData.category}
              onChange={(e) => handleChange("category", null, e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
            >
              <option value="startup">Startup</option>
              <option value="research">Research Project</option>
              <option value="innovation">Innovation</option>
              <option value="incubation">Incubation</option>
              <option value="community">Community Project</option>
              <option value="infrastructure">Infrastructure</option>
              <option value="scholarship">Scholarship</option>
              <option value="other">Other</option>
            </select>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-gray-700 block mb-1.5">
              Subcategory
            </span>
            <input
              type="text"
              value={formData.subcategory}
              onChange={(e) =>
                handleChange("subcategory", null, e.target.value)
              }
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
              placeholder="Specific focus area"
            />
          </label>
        </div>
      </div>

      <div className="space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-gray-700 block mb-1.5">
            Short Description
          </span>
          <textarea
            value={formData.description.short}
            onChange={(e) =>
              handleChange("description", "short", e.target.value)
            }
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
            rows={3}
            placeholder="Brief overview of your campaign"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700 block mb-1.5">
            Detailed Description
          </span>
          <textarea
            value={formData.description.detailed}
            onChange={(e) =>
              handleChange("description", "detailed", e.target.value)
            }
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
            rows={6}
            placeholder="Comprehensive description of your campaign"
          />
        </label>
      </div>
    </div>
  );

  const renderTimeline = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <label className="block">
          <span className="text-sm font-medium text-gray-700 block mb-1.5">
            Start Date
          </span>
          <div className="relative">
            <Calendar className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              type="date"
              value={formData.timeline.startDate}
              onChange={(e) =>
                handleChange("timeline", "startDate", e.target.value)
              }
              className="w-full pl-12 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700 block mb-1.5">
            End Date
          </span>
          <div className="relative">
            <Calendar className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              type="date"
              value={formData.timeline.endDate}
              onChange={(e) =>
                handleChange("timeline", "endDate", e.target.value)
              }
              className="w-full pl-12 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </label>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Milestones</h3>
          <button
            type="button"
            onClick={addMilestone}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
          >
            <PlusCircle className="h-4 w-4 mr-1.5" />
            Add Milestone
          </button>
        </div>

        {formData.timeline.milestones.map((milestone, index) => (
          <div key={index} className="p-4 bg-gray-50 rounded-lg space-y-4">
            <div className="flex justify-between items-start">
              <div className="flex-1 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={milestone.title}
                    onChange={(e) =>
                      updateMilestone(index, "title", e.target.value)
                    }
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                    placeholder="Milestone title"
                  />

                  <input
                    type="date"
                    value={milestone.targetDate}
                    onChange={(e) =>
                      updateMilestone(index, "targetDate", e.target.value)
                    }
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <textarea
                  value={milestone.description}
                  onChange={(e) =>
                    updateMilestone(index, "description", e.target.value)
                  }
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                  placeholder="Milestone description"
                  rows={2}
                />
              </div>

              <button
                type="button"
                onClick={() => removeMilestone(index)}
                className="ml-2 p-1 text-gray-400 hover:text-red-500"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
  const renderFinancials = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <label className="block">
          <span className="text-sm font-medium text-gray-700 block mb-1.5">
            Target Amount (₹)
          </span>
          <div className="relative">
            <Gift className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              type="number"
              value={formData.financials.targetAmount}
              onChange={(e) =>
                handleChange("financials", "targetAmount", e.target.value)
              }
              className="w-full pl-12 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
              placeholder="0.00"
            />
          </div>
        </label>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            Budget Breakdown
          </h3>
          <button
            type="button"
            onClick={addBudgetItem}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
          >
            <PlusCircle className="h-4 w-4 mr-1.5" />
            Add Budget Item
          </button>
        </div>

        {formData.financials.budget.map((item, index) => (
          <div key={index} className="p-4 bg-gray-50 rounded-lg flex gap-4">
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="text"
                value={item.category}
                onChange={(e) =>
                  updateBudgetItem(index, "category", e.target.value)
                }
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                placeholder="Category"
              />
              <input
                type="number"
                value={item.amount}
                onChange={(e) =>
                  updateBudgetItem(index, "amount", e.target.value)
                }
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                placeholder="Amount"
              />
              <input
                type="text"
                value={item.description}
                onChange={(e) =>
                  updateBudgetItem(index, "description", e.target.value)
                }
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                placeholder="Description"
              />
            </div>
            <button
              type="button"
              onClick={() => removeBudgetItem(index)}
              className="p-1 text-gray-400 hover:text-red-500"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        ))}
      </div>

      {/* Rewards Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Rewards</h3>
          <button
            type="button"
            onClick={() => {
              setFormData((prev) => ({
                ...prev,
                financials: {
                  ...prev.financials,
                  rewards: [
                    ...prev.financials.rewards,
                    { threshold: "", description: "" },
                  ],
                },
              }));
            }}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
          >
            <PlusCircle className="h-4 w-4 mr-1.5" />
            Add Reward
          </button>
        </div>

        {formData.financials.rewards.map((reward, index) => (
          <div key={index} className="p-4 bg-gray-50 rounded-lg flex gap-4">
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">
                  Contribution Threshold
                </label>
                <input
                  type="number"
                  value={reward.threshold}
                  onChange={(e) => {
                    const newRewards = [...formData.financials.rewards];
                    newRewards[index].threshold = e.target.value;
                    handleChange("financials", "rewards", newRewards);
                  }}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                  placeholder="Minimum amount"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">
                  Reward Description
                </label>
                <input
                  type="text"
                  value={reward.description}
                  onChange={(e) => {
                    const newRewards = [...formData.financials.rewards];
                    newRewards[index].description = e.target.value;
                    handleChange("financials", "rewards", newRewards);
                  }}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                  placeholder="What will contributors receive?"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                const newRewards = formData.financials.rewards.filter(
                  (_, i) => i !== index
                );
                handleChange("financials", "rewards", newRewards);
              }}
              className="p-1 text-gray-400 hover:text-red-500"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderTeam = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Team Members Needed
        </h3>
        <button
          type="button"
          onClick={addTeamMember}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
        >
          <PlusCircle className="h-4 w-4 mr-1.5" />
          Add Team Member
        </button>
      </div>

      {formData.team.map((member, index) => (
        <div key={index} className="p-6 bg-gray-50 rounded-lg space-y-4">
          <div className="flex justify-between items-start">
            <div className="flex-1 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={member?.role || ""}
                  onChange={(e) =>
                    updateTeamMember(index, "role", e.target.value)
                  }
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                  placeholder="Role title"
                />
                <input
                  type="text"
                  value={member?.experience || ""}
                  onChange={(e) =>
                    updateTeamMember(index, "experience", e.target.value)
                  }
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                  placeholder="Required experience"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">
                  Required Skills
                </label>
                <input
                  type="text"
                  value={(member?.skills || []).join(", ")}
                  onChange={(e) =>
                    updateTeamMember(
                      index,
                      "skills",
                      e.target.value.split(",").map((s) => s.trim())
                    )
                  }
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter skills separated by commas"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">
                  Responsibilities
                </label>
                <textarea
                  value={(member?.responsibilities || []).join("\n")}
                  onChange={(e) =>
                    updateTeamMember(
                      index,
                      "responsibilities",
                      e.target.value.split("\n")
                    )
                  }
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Enter each responsibility on a new line"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => removeTeamMember(index)}
              className="ml-2 p-1 text-gray-400 hover:text-red-500"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderProjectDetails = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700 block mb-1.5">
              Demo Link
            </span>
            <div className="relative">
              <Globe className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="url"
                value={formData.projectLinks.demo}
                onChange={(e) =>
                  handleChange("projectLinks", "demo", e.target.value)
                }
                className="w-full pl-12 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                placeholder="https://"
              />
            </div>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-gray-700 block mb-1.5">
              GitHub Repository
            </span>
            <div className="relative">
              <Github className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="url"
                value={formData.projectLinks.github}
                onChange={(e) =>
                  handleChange("projectLinks", "github", e.target.value)
                }
                className="w-full pl-12 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                placeholder="https://github.com/"
              />
            </div>
          </label>
        </div>

        <div className="space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700 block mb-1.5">
              Drive Folder
            </span>
            <div className="relative">
              <Database className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="url"
                value={formData.projectLinks.driveFolder}
                onChange={(e) =>
                  handleChange("projectLinks", "driveFolder", e.target.value)
                }
                className="w-full pl-12 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                placeholder="https://drive.google.com/"
              />
            </div>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-gray-700 block mb-1.5">
              Design Files
            </span>
            <div className="relative">
              <Image className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="url"
                value={formData.projectLinks.designFiles}
                onChange={(e) =>
                  handleChange("projectLinks", "designFiles", e.target.value)
                }
                className="w-full pl-12 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                placeholder="Figma/Adobe XD link"
              />
            </div>
          </label>
        </div>
      </div>

      {(formData.category === "startup" ||
        formData.category === "innovation") && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">
            Technical Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                Tech Stack
              </label>
              <input
                type="text"
                value={formData.technicalDetails.stack.join(", ")}
                onChange={(e) =>
                  handleChange(
                    "technicalDetails",
                    "stack",
                    e.target.value.split(",").map((s) => s.trim())
                  )
                }
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                placeholder="Technologies used, separated by commas"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                APIs Used
              </label>
              <input
                type="text"
                value={formData.technicalDetails.apis.join(", ")}
                onChange={(e) =>
                  handleChange(
                    "technicalDetails",
                    "apis",
                    e.target.value.split(",").map((s) => s.trim())
                  )
                }
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                placeholder="APIs integrated, separated by commas"
              />
            </div>
          </div>

          <label className="block">
            <span className="text-sm font-medium text-gray-700 block mb-1.5">
              Architecture Overview
            </span>
            <textarea
              value={formData.technicalDetails.architecture}
              onChange={(e) =>
                handleChange("technicalDetails", "architecture", e.target.value)
              }
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Brief description of your technical architecture"
            />
          </label>
        </div>
      )}
    </div>
  );

  const renderImpactAndGoals = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Impact Metrics</h3>
          <button
            type="button"
            onClick={() => {
              setFormData((prev) => ({
                ...prev,
                impact: {
                  ...prev.impact,
                  metrics: [
                    ...prev.impact.metrics,
                    { name: "", target: "", current: "" },
                  ],
                },
              }));
            }}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
          >
            <PlusCircle className="h-4 w-4 mr-1.5" />
            Add Metric
          </button>
        </div>

        {formData.impact.metrics.map((metric, index) => (
          <div key={index} className="p-4 bg-gray-50 rounded-lg flex gap-4">
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="text"
                value={metric.name}
                onChange={(e) => {
                  const newMetrics = [...formData.impact.metrics];
                  newMetrics[index].name = e.target.value;
                  handleChange("impact", "metrics", newMetrics);
                }}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                placeholder="Metric name"
              />
              <input
                type="number"
                value={metric.target}
                onChange={(e) => {
                  const newMetrics = [...formData.impact.metrics];
                  newMetrics[index].target = e.target.value;
                  handleChange("impact", "metrics", newMetrics);
                }}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                placeholder="Target value"
              />
              <input
                type="number"
                value={metric.current}
                onChange={(e) => {
                  const newMetrics = [...formData.impact.metrics];
                  newMetrics[index].current = e.target.value;
                  handleChange("impact", "metrics", newMetrics);
                }}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                placeholder="Current value"
              />
            </div>
            <button
              type="button"
              onClick={() => {
                const newMetrics = formData.impact.metrics.filter(
                  (_, i) => i !== index
                );
                handleChange("impact", "metrics", newMetrics);
              }}
              className="p-1 text-gray-400 hover:text-red-500"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        ))}
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Beneficiaries</h3>
        <div className="p-4 bg-gray-50 rounded-lg">
          <input
            type="text"
            value={formData.impact.beneficiaries.join(", ")}
            onChange={(e) =>
              handleChange(
                "impact",
                "beneficiaries",
                e.target.value.split(",").map((b) => b.trim())
              )
            }
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
            placeholder="Who will benefit from this project? (separate by commas)"
          />
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Expected Outcomes</h3>
        <div className="p-4 bg-gray-50 rounded-lg">
          <textarea
            value={formData.impact.outcomes.join("\n")}
            onChange={(e) =>
              handleChange("impact", "outcomes", e.target.value.split("\n"))
            }
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
            rows={4}
            placeholder="Enter each expected outcome on a new line"
          />
        </div>
      </div>
    </div>
  );

  const renderMedia = () => (
    <div className="space-y-6">
      {/* Hero Image Upload Section */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Campaign Images</h3>
              <p className="text-sm text-gray-500 mt-1">Add images to showcase your campaign</p>
            </div>
            <ImageIcon className="h-5 w-5 text-gray-400" />
          </div>
  
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
  {[...Array(3)].map((_, index) => (
    <div
      key={index}
      className="relative aspect-video bg-gray-50 rounded-lg overflow-hidden"
    >
      <ImageUpload
        onImageUpload={(url) => {
          const newImages = [...formData.media.images];
          // Format the image data according to the schema
          newImages[index] = {
            url: url,
            caption: `Image ${index + 1}`,
            isPrimary: index === 0
          };
          handleChange("media", "images", newImages);
        }}
        existingImage={formData.media.images[index]?.url} // Access the URL from the object
        className="w-full h-full object-cover"
      />
      <div className="absolute top-2 left-2 px-2 py-1 bg-black/50 rounded text-xs text-white">
        {index === 0 ? "Cover Image" : `Image ${index + 1}`}
      </div>
    </div>
  ))}
</div>
        </div>
      </div>
  
      {/* Videos Section */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Video Links</h3>
              <p className="text-sm text-gray-500 mt-1">Add links to campaign-related videos</p>
            </div>
            <button
              type="button"
              onClick={() => {
                const newVideos = [...formData.media.videos, { url: "", title: "" }];
                handleChange("media", "videos", newVideos);
              }}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
            >
              <PlusCircle className="h-4 w-4 mr-1.5" />
              Add Video
            </button>
          </div>
  
          <div className="space-y-4">
            {formData.media.videos.map((video, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4 flex gap-4">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="url"
                    value={video.url}
                    onChange={(e) => {
                      const newVideos = [...formData.media.videos];
                      newVideos[index].url = e.target.value;
                      handleChange("media", "videos", newVideos);
                    }}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                    placeholder="Video URL"
                  />
                  <input
                    type="text"
                    value={video.title}
                    onChange={(e) => {
                      const newVideos = [...formData.media.videos];
                      newVideos[index].title = e.target.value;
                      handleChange("media", "videos", newVideos);
                    }}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                    placeholder="Video title"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const newVideos = formData.media.videos.filter((_, i) => i !== index);
                    handleChange("media", "videos", newVideos);
                  }}
                  className="p-1 text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
  
      {/* Documents Section */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Documents</h3>
              <p className="text-sm text-gray-500 mt-1">Upload campaign-related documents</p>
            </div>
            <button
              type="button"
              onClick={() => {
                const newDocs = [...formData.media.documents, { url: "", title: "", type: "" }];
                handleChange("media", "documents", newDocs);
              }}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
            >
              <PlusCircle className="h-4 w-4 mr-1.5" />
              Add Document
            </button>
          </div>
  
          <div className="space-y-4">
            {formData.media.documents.map((doc, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4 flex gap-4">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <input
                    type="url"
                    value={doc.url}
                    onChange={(e) => {
                      const newDocs = [...formData.media.documents];
                      newDocs[index].url = e.target.value;
                      handleChange("media", "documents", newDocs);
                    }}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                    placeholder="Document URL"
                  />
                  <input
                    type="text"
                    value={doc.title}
                    onChange={(e) => {
                      const newDocs = [...formData.media.documents];
                      newDocs[index].title = e.target.value;
                      handleChange("media", "documents", newDocs);
                    }}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                    placeholder="Document title"
                  />
                  <select
                    value={doc.type}
                    onChange={(e) => {
                      const newDocs = [...formData.media.documents];
                      newDocs[index].type = e.target.value;
                      handleChange("media", "documents", newDocs);
                    }}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select type</option>
                    <option value="proposal">Proposal</option>
                    <option value="presentation">Presentation</option>
                    <option value="report">Report</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const newDocs = formData.media.documents.filter((_, i) => i !== index);
                    handleChange("media", "documents", newDocs);
                  }}
                  className="p-1 text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
  const renderNavigationButtons = () => (
    <div className="flex items-center justify-between pt-6">
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
          }
        }}
        className={`px-6 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 
          flex items-center ${currentStep === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
        disabled={currentStep === 1}
      >
        Previous
      </button>

      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            navigate('/campaigns');
          }}
          className="px-6 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>

        {currentStep < steps.length ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setCurrentStep(currentStep + 1);
            }}
            className="px-6 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 
              flex items-center"
          >
            Next Step <ChevronRight className="ml-2 h-4 w-4" />
          </button>
        ) : (
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 
              flex items-center disabled:bg-blue-400"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin mr-2 h-5 w-5" />
                Creating Campaign...
              </>
            ) : (
              <>
                <Save className="mr-2 h-5 w-5" />
                {id ? 'Update Campaign' : 'Create Campaign'}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );

  // Main Form Return
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {id ? "Edit Campaign" : "Create New Campaign"}
          </h1>
          <p className="mt-2 text-gray-600">
            {id
              ? "Update your campaign details"
              : "Launch a new fundraising campaign"}
          </p>
        </div>
        {/* Progress Steps */}
        <div className="mb-8 p-4 bg-white rounded-xl shadow-sm">
          <div className="flex justify-between">
            {steps.map((step) => (
              <div
                key={step.id}
                className={`flex flex-col items-center ${
                  currentStep === step.id
                    ? "text-blue-600"
                    : currentStep > step.id
                    ? "text-green-600"
                    : "text-gray-400"
                }`}
              >
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-full border-2 mb-2
                  ${
                    currentStep === step.id
                      ? "border-blue-600 bg-blue-50"
                      : currentStep > step.id
                      ? "border-green-600 bg-green-50"
                      : "border-gray-300 bg-white"
                  }`}
                >
                  <step.icon className="w-5 h-5" />
                </div>
                <span className="text-xs font-medium hidden md:block">
                  {step.title}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {currentStep === 1 && renderBasicDetails()}
            {currentStep === 2 && renderTimeline()}
            {currentStep === 3 && renderFinancials()}
            {currentStep === 4 && renderTeam()}
            {currentStep === 5 && renderProjectDetails()}
            {currentStep === 6 && renderImpactAndGoals()}
            {currentStep === 7 && renderMedia()}

            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  {error}
                </div>
              </div>
            )}

{renderNavigationButtons()}
          </form>
        </div>
      </div>
    </div>
  );
};

export default CampaignForm;
